import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Star, Clock, Gift, CreditCard, Trophy, ArrowLeft, CheckCircle2, ImageIcon, Users, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const Section = ({ title, Icon, items, render, testid }) => (
  <div className="bg-white rounded-xl border p-5" data-testid={testid}>
    <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-secondary" /> {title} <span className="text-gray-400 text-sm">({items.length})</span></h3>
    {items.length === 0 ? <p className="text-sm text-gray-400">None</p> : <div className="space-y-2">{items.map(render)}</div>}
  </div>
);

const AdminFeatured = () => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('Please upload a clearer professional photo or logo.');

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/admin/featured/dashboard`); setD(r.data); }
    catch (e) { toast.error('Could not load Featured dashboard'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (userId) => {
    setBusy(userId);
    try { await axios.post(`${API_URL}/admin/featured/approve-reward/${userId}`, {}); toast.success('30-day extension approved'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not approve'); } finally { setBusy(''); }
  };

  const approveRequest = async (mid) => {
    setBusy(mid);
    try { await axios.post(`${API_URL}/admin/featured/approve-request/${mid}`); toast.success('Featured approved — congrats email sent'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not approve'); } finally { setBusy(''); }
  };

  const rejectRequest = (mid) => {
    setRejectReason('Please upload a clearer professional photo or logo.');
    setRejectTarget(mid);
  };

  const confirmReject = async () => {
    const mid = rejectTarget;
    const reason = rejectReason.trim();
    setBusy(mid);
    try {
      await axios.post(`${API_URL}/admin/featured/reject-request/${mid}`, { reason });
      toast.success('Request rejected — user notified');
      setRejectTarget(null);
      await load();
    }
    catch (e) { toast.error('Could not reject'); } finally { setBusy(''); }
  };

  if (loading) return <div className="min-h-screen pt-28 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const row = (m) => (
    <div key={m.membership_id || m.user_id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg text-sm">
      <div>
        <p className="font-medium text-gray-900">{m.account?.name || m.name || m.listing_title}</p>
        <p className="text-xs text-gray-500">{m.account?.email || m.email} · {m.listing_title || ''}</p>
      </div>
      <div className="text-right">
        <Badge className="bg-secondary/15 text-secondary border-secondary/30 capitalize">{m.period_type || ''}</Badge>
        <p className="text-xs text-gray-500 mt-1">until {fmt(m.expires_at)}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16" data-testid="admin-featured-page">
      <section className="bg-primary pt-24 pb-6 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Link to="/admin" className="text-secondary text-sm hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Admin</Link>
          <h1 className="font-serif text-3xl font-bold mt-2 flex items-center gap-2"><Star className="w-6 h-6 text-secondary" /> Featured Program</h1>
          <p className="text-white/80 mt-1 text-sm">Rewards earned to date: {d?.rewards_earned_total ?? 0}</p>
          <Link to="/admin/featured/rollout" className="inline-block mt-3"><Button size="sm" variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20" data-testid="open-rollout">Existing-User Rollout →</Button></Link>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        {/* Featured Requests Awaiting Approval (photo/logo + approve/reject) */}
        <div className="bg-white rounded-xl border p-5" data-testid="requests-awaiting">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><ImageIcon className="w-4 h-4 text-secondary" /> Featured Requests &amp; Photos Awaiting Approval <span className="text-gray-400 text-sm">({d?.requests_awaiting_approval?.length || 0})</span></h3>
          {(!d?.requests_awaiting_approval || d.requests_awaiting_approval.length === 0) ? (
            <p className="text-sm text-gray-400">No requests pending</p>
          ) : d.requests_awaiting_approval.map((m) => (
            <div key={m.membership_id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-2" data-testid={`request-${m.membership_id}`}>
              {m.photo_url
                ? <img src={m.photo_url} alt="uploaded" className="w-14 h-14 rounded-lg object-cover border" data-testid={`request-photo-${m.membership_id}`} />
                : <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center"><ImageIcon className="w-5 h-5 text-gray-300" /></div>}
              <div className="flex-1 text-sm">
                <p className="font-medium text-gray-900">{m.account?.name} <span className="text-gray-500 font-normal">· {m.account?.email}</span></p>
                <p className="text-xs text-gray-600 capitalize">{m.listing_type?.replace('_', ' ')} · {m.listing_title}</p>
                <p className="text-[11px] text-gray-400">Requested {fmt(m.submitted_at)}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy === m.membership_id} onClick={() => approveRequest(m.membership_id)} data-testid={`approve-request-${m.membership_id}`}>
                  {busy === m.membership_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve Featured'}
                </Button>
                <Button size="sm" variant="outline" disabled={busy === m.membership_id} onClick={() => rejectRequest(m.membership_id)} data-testid={`reject-request-${m.membership_id}`}>Reject / New Photo</Button>
              </div>
            </div>
          ))}
        </div>

        {/* Extensions awaiting approval (reward engine — background) */}
        <div className="bg-white rounded-xl border p-5" data-testid="awaiting-approval">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-amber-500" /> Extensions Awaiting Approval <span className="text-gray-400 text-sm">({d?.extensions_awaiting_approval?.length || 0})</span></h3>
          {(!d?.extensions_awaiting_approval || d.extensions_awaiting_approval.length === 0) ? (
            <p className="text-sm text-gray-400">No rewards pending</p>
          ) : d.extensions_awaiting_approval.map((p) => (
            <div key={p.user_id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg mb-2" data-testid={`pending-${p.user_id}`}>
              <div className="text-sm">
                <p className="font-medium text-gray-900">{p.name} <span className="text-gray-500 font-normal">· {p.email}</span></p>
                <p className="text-xs text-gray-600">{p.reward_resources} approved resources · {p.reward_referrals} verified referrals</p>
                <p className="text-[11px] text-gray-400 break-all">res: {(p.counted_resource_ids || []).join(', ')}</p>
              </div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy === p.user_id} onClick={() => approve(p.user_id)} data-testid={`approve-${p.user_id}`}>
                {busy === p.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve 30-Day Extension'}
              </Button>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <Section title="Currently Featured" Icon={Star} items={d?.currently_featured || []} render={row} testid="sec-current" />
          <Section title="Expiring Soon (7 days)" Icon={Clock} items={d?.expiring_soon || []} render={row} testid="sec-expiring" />
          <Section title="Free 30-Day Accounts" Icon={Gift} items={d?.free_accounts || []} render={row} testid="sec-free" />
          <Section title="Paid / Reward Accounts" Icon={Star} items={d?.paid_accounts || []} render={row} testid="sec-paid" />
          <Section title="Expired Featured" Icon={Clock} items={d?.expired || []} render={row} testid="sec-expired" />
          <Section title="Missing Payment Method" Icon={CreditCard} items={d?.missing_card || []} render={row} testid="sec-missing-card" />
        </div>

        {/* Background engagement — resource & referral activity (not required for initial Featured) */}
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border p-5" data-testid="resource-activity">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-secondary" /> Resource Activity</h3>
            <p className="text-sm text-gray-600">{d?.resource_activity?.lifetime_resources ?? 0} approved resources contributed across {d?.resource_activity?.accounts_with_resources ?? 0} accounts.</p>
            <p className="text-xs text-gray-400 mt-1">Tracked for later rewards — not required for the initial 30-day Featured.</p>
          </div>
          <div className="bg-white rounded-xl border p-5" data-testid="referral-activity">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-secondary" /> Referral Activity</h3>
            <p className="text-sm text-gray-600">{d?.referral_activity?.total_referrals ?? 0} student/intern referrals across {d?.referral_activity?.accounts_with_referrals ?? 0} accounts.</p>
            <p className="text-xs text-gray-400 mt-1">Tracked for later rewards — not required for the initial 30-day Featured.</p>
          </div>
        </div>
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent className="bg-white" data-testid="reject-request-dialog">
          <DialogHeader>
            <DialogTitle>Reject Featured Request</DialogTitle>
            <DialogDescription>
              Let the user know why, or request a different photo/logo. This message is emailed to them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason / note to user</Label>
            <Textarea
              id="reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Please upload a clearer professional photo or logo."
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} data-testid="reject-cancel-btn">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={busy === rejectTarget} onClick={confirmReject} data-testid="reject-confirm-btn">
              {busy === rejectTarget ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject & Notify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFeatured;
